import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from '@/redux/store'
import { markNotificationAsRead } from '@/redux/asyncThunks/notification'
import { formatDistanceToNow } from 'date-fns'
import { Button } from '../ui/button'
import { useNavigate } from 'react-router-dom'
import { Notification } from '@/redux/types/notifications'
import { cn } from '@/lib/utils'

interface NotificationPanelProps {
  notifications: Notification[]
  onMarkAllAsRead: () => void
}

const NotificationPanel: React.FC<NotificationPanelProps> = ({
  notifications,
  onMarkAllAsRead,
}) => {
  const dispatch = useDispatch<AppDispatch>()
  const navigate = useNavigate()
  const { unreadCount } = useSelector((state: RootState) => state.notifications)

  const handleClickNotification = (notification: any) => {
    dispatch(
      markNotificationAsRead({
        notificationId: notification.id,
      })
    )
    if (notification.data.action_url) {
      navigate(notification.action_url)
    }
  }

  const renderNotificationItem = (notification: any) => {
    const timeAgo = formatDistanceToNow(new Date(notification.created_at), {
      addSuffix: true,
    })

    return (
      <div
        key={notification.id}
        className={cn(
          'p-3 border-b border-gray-200 hover:bg-[#E6ECF2] cursor-pointer',
          !notification.is_read && 'bg-[#02357217]'
        )}
        onClick={() => handleClickNotification(notification)}
      >
        <div className='flex items-start gap-3'>
          <div className='flex-1 min-w-0'>
            <p className='text-sm font-medium text-[#023572]'>
              {notification.title}
            </p>
            <p className='mt-1 text-xs text-gray-600'>{notification.message}</p>
            <p className='mt-1 text-xs text-gray-500'>{timeAgo}</p>
          </div>
          {!notification.is_read && (
            <div className='flex-shrink-0 w-2 h-2 bg-blue-500 rounded-full'></div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className='flex flex-col max-h-[80vh]'>
      <div className='flex items-center justify-between p-3 border-b border-gray-200'>
        <h3 className='text-base font-medium text-[#023572]'>Notifications</h3>
      </div>
      <div className='max-h-[350px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-100'>
        {notifications.length === 0 ? (
          <div className='p-4 text-center text-gray-500'>No notifications</div>
        ) : (
          <div className='pb-1'>
            {notifications.map(renderNotificationItem)}
          </div>
        )}
      </div>
      {unreadCount > 0 && (
        <div className='flex justify-end p-3 border-t border-gray-200 bg-[#D5DEEBD9]'>
          <Button
            variant='ghost'
            size='sm'
            onClick={onMarkAllAsRead}
            className='text-xs text-[#023572] hover:text-blue-700'
          >
            Mark all as read
          </Button>
        </div>
      )}
    </div>
  )
}

export default NotificationPanel
